/**
 * FocusMode — hold/toggle Alt+F to dim everything on the page except the
 * currently-hovered / currently-focused card. Premium feel. Exits on Esc.
 *
 * Implementation:
 *   • On toggle ON: adds `focus-mode` class to <body>.
 *   • While on, hovered `.lms-card`, `.surface-soft`, `.portal-surface`, or
 *     `[data-focusable="true"]` element gets `.focus-mode__spot` class which
 *     restores full opacity; everything else is dimmed.
 *   • On Esc or second Alt+F press: exits.
 */
import { useEffect } from "react";

export default function FocusMode() {
  useEffect(() => {
    let on = false;
    const toggle = (val) => {
      on = val;
      if (val) {
        document.body.classList.add("focus-mode");
        // Show a tiny toast
        showHint(true);
      } else {
        document.body.classList.remove("focus-mode");
        document.querySelectorAll(".focus-mode__spot").forEach((n) => n.classList.remove("focus-mode__spot"));
        showHint(false);
      }
    };

    const key = (e) => {
      if (e.altKey && (e.key === "f" || e.key === "F" || e.code === "KeyF")) {
        e.preventDefault();
        toggle(!on);
      } else if (e.key === "Escape" && on) {
        toggle(false);
      }
    };

    const over = (e) => {
      if (!on) return;
      const el = e.target.closest(".lms-card, .surface-soft, .portal-surface, [data-focusable=\"true\"]");
      document.querySelectorAll(".focus-mode__spot").forEach((n) => n.classList.remove("focus-mode__spot"));
      if (el) el.classList.add("focus-mode__spot");
    };

    window.addEventListener("keydown", key);
    window.addEventListener("mouseover", over);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener("mouseover", over);
      toggle(false);
    };
  }, []);

  return null;
}

function showHint(show) {
  let el = document.getElementById("lw-focus-hint");
  if (show) {
    if (!el) {
      el = document.createElement("div");
      el.id = "lw-focus-hint";
      el.setAttribute("data-testid", "focus-mode-hint");
      el.innerHTML = `
        <span style="opacity:.7">Focus mode ·</span> hover a card
        <kbd style="margin-left:8px;padding:2px 6px;border-radius:6px;background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);font-family:ui-monospace,monospace;font-size:10px;">ESC</kbd>
      `;
      el.style.cssText = "position:fixed;bottom:18px;left:50%;transform:translateX(-50%) translateY(20px);background:#0f172a;color:#f1f5f9;font-family:Inter,system-ui;font-size:12px;font-weight:500;padding:8px 14px;border-radius:999px;z-index:9999;box-shadow:0 20px 40px -12px rgba(0,0,0,0.35);opacity:0;transition:opacity 220ms ease, transform 220ms ease;";
      document.body.appendChild(el);
    }
    requestAnimationFrame(() => {
      el.style.opacity = "1";
      el.style.transform = "translateX(-50%) translateY(0)";
    });
  } else if (el) {
    el.style.opacity = "0";
    el.style.transform = "translateX(-50%) translateY(20px)";
    setTimeout(() => el?.remove(), 240);
  }
}
